import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { AspectRatio } from '../../types.ts/video';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import {
  type AspectRatioStatus,
  STATUS_CONFIG,
  StatusBadge,
  StatusSpinner,
} from '../stage/AspectRatioStatusBadge';
import {
  AspectDropDown,
  AspectItem,
  AspectItemContent,
  AspectRatioLogo,
  AspectRow,
  CaretDownIcon,
  DropdownIcon,
  HorizontalAspectIcon,
  SelectedAspectRatio,
  SquareAspectIcon,
  VerticalAspectIcon,
} from '../stage/AspectRatioUiPrimitives';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import { type DimensionItem } from './DimensionsSelector';

type Props = {
  dimensions: DimensionItem[];
  selectedDimension: string;
  onSelectDimension: (dimension: string) => void;
  /**
   * All aspect ratios with a render currently in flight. Each AR is a separate
   * video the backend renders concurrently, so more than one row can be
   * "publishing" at once — this set is the source of truth for the publishing
   * badge/spinner on every row.
   */
  publishingDimensions?: ReadonlySet<string>;
  /**
   * The single publish the current user just started, if any. Its
   * `startTime`/`by` detail is shown in the tooltip for that one row only —
   * other concurrently-publishing rows (registered via the store) just show
   * "Publishing..." since this detail doesn't describe them.
   */
  localPublish?: {
    aspectRatio: string;
    startTime?: Date | null;
    by?: string;
  };
  disabled?: boolean;
  disabledTooltip?: string;
};

function getStatus(
  dimension: DimensionItem,
  publishingDimensions?: ReadonlySet<string>,
): AspectRatioStatus {
  if (publishingDimensions?.has(dimension.aspectRatio)) return 'publishing';
  if (dimension.isCreating) return 'creating';
  if (dimension.isNew) return 'new';
  if (dimension.isPublished) return 'final';
  return 'draft';
}

function AspectRatioIcon({
  aspectRatio,
  isSelected,
}: {
  aspectRatio: string;
  isSelected?: boolean;
}) {
  switch (aspectRatio) {
    case AspectRatio.AR_16_9:
      return <HorizontalAspectIcon isSelected={isSelected} />;
    case AspectRatio.AR_1_1:
      return <SquareAspectIcon isSelected={isSelected} />;
    case AspectRatio.AR_9_16:
      return <VerticalAspectIcon isSelected={isSelected} />;
    default:
      return <HorizontalAspectIcon isSelected={isSelected} />;
  }
}

const ClipDimensionsDropdown = ({
  dimensions,
  selectedDimension,
  onSelectDimension,
  publishingDimensions,
  localPublish,
  disabled,
  disabledTooltip,
}: Props) => {
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const disabledTooltipId = 'clip-dimensions-disabled-tooltip';

  // Close on outside click — must check both the trigger area and the portaled menu
  useOutsideAlerter(dropdownRef, (event: MouseEvent) => {
    if (
      menuRef.current &&
      event.target instanceof Node &&
      menuRef.current.contains(event.target)
    ) {
      return;
    }
    setMenuRect(null);
  });

  const selectedItem = dimensions.find(
    d => d.aspectRatio === selectedDimension,
  );
  const selectedStatus = selectedItem
    ? getStatus(selectedItem, publishingDimensions)
    : 'draft';

  return (
    <Main ref={dropdownRef}>
      <AspectItem
        onClick={e => {
          if (disabled) return;
          const rect = e.currentTarget.getBoundingClientRect();
          setMenuRect(prev => (prev ? null : rect));
        }}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        data-tooltip-id={
          disabled && disabledTooltip ? disabledTooltipId : undefined
        }
        data-tooltip-content={
          disabled && disabledTooltip ? disabledTooltip : undefined
        }
      >
        <AspectItemContent>
          <AspectRatioLogo>
            <AspectRatioIcon aspectRatio={selectedDimension} />
          </AspectRatioLogo>
          <SelectedAspectRatio>{selectedDimension}</SelectedAspectRatio>
        </AspectItemContent>
        <StatusBadge status={selectedStatus}>
          {selectedStatus === 'publishing' && <StatusSpinner />}
          {STATUS_CONFIG[selectedStatus].text}
        </StatusBadge>
        <DropdownIcon isOpen={!!menuRect}>
          <CaretDownIcon />
        </DropdownIcon>
      </AspectItem>

      {disabled && disabledTooltip && (
        <Tooltip
          id={disabledTooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
          noArrow
        />
      )}

      {menuRect &&
        createPortal(
          <ClipAspectDropDown
            ref={menuRef}
            style={{ top: menuRect.bottom + 2, left: menuRect.left }}
          >
            {dimensions.map(dimension => {
              const status = getStatus(dimension, publishingDimensions);
              const isSelected = dimension.aspectRatio === selectedDimension;
              const isDisabled = status === 'creating';
              const isPublishing = status === 'publishing';
              // `localPublish` describes only the publish the current user just
              // started. Other rows can be publishing concurrently via the
              // store, so only attach the "Started … / By …" detail to the row
              // that matches it; the rest just show "Publishing...".
              const isLocalPublish =
                dimension.aspectRatio === localPublish?.aspectRatio;
              const tooltipId = `clip-dim-${dimension.aspectRatio}-tooltip`;
              const tooltipContent = isPublishing
                ? [
                    'Publishing...',
                    isLocalPublish &&
                      localPublish?.startTime &&
                      `Started ${localPublish.startTime.toLocaleTimeString()}`,
                    isLocalPublish &&
                      localPublish?.by &&
                      `By ${localPublish.by}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : undefined;

              return (
                <ClipAspectRow
                  key={dimension.aspectRatio}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    onSelectDimension(dimension.aspectRatio);
                    setMenuRect(null);
                  }}
                  data-tooltip-id={tooltipContent ? tooltipId : undefined}
                  data-tooltip-content={tooltipContent}
                >
                  <AspectRatioLogo>
                    <AspectRatioIcon
                      aspectRatio={dimension.aspectRatio}
                      isSelected={isSelected}
                    />
                  </AspectRatioLogo>
                  <RowLabel>{dimension.aspectRatio}</RowLabel>
                  <StatusBadge status={status}>
                    {isPublishing && <StatusSpinner />}
                    {STATUS_CONFIG[status].text}
                  </StatusBadge>
                  {tooltipContent && (
                    <Tooltip
                      id={tooltipId}
                      style={COMPACT_TOOLTIP_STYLE}
                      place="right"
                      positionStrategy="fixed"
                      noArrow
                    />
                  )}
                </ClipAspectRow>
              );
            })}
          </ClipAspectDropDown>,
          document.body,
        )}
    </Main>
  );
};

export default ClipDimensionsDropdown;

// --- Styled components ---

const Main = styled.div`
  position: relative;
`;

/** Portaled to document.body with position: fixed to escape the Modal stacking
 *  context (1000005). z-index 1000008 sits above the renderer (1000006) and
 *  the WarmerOverlayWrapper (1000007). */
const ClipAspectDropDown = styled(AspectDropDown)`
  position: fixed;
  width: 200px;
  z-index: ${CLIP_PREVIEW_Z.modalPopover};
`;

const ClipAspectRow = styled(AspectRow)<{ isDisabled?: boolean }>`
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
`;

const RowLabel = styled.span`
  white-space: nowrap;
`;
